import { HorizontalTabs } from 'common-front/src/designSystem/components/horizontalTabs/horizontalTabs';
import { Tab } from 'common-front/src/designSystem/components/horizontalTabs/tab';
import { useHeavent } from 'common-front/src/hooks/useHeavent';
import { Avatar } from 'common/src/components/avatar/avatar';
import { Badge, IBadgeColorIcon } from 'common/src/designSystem/components/badge';
import { Box } from 'common/src/designSystem/components/box';
import { Flex } from 'common/src/designSystem/components/flex';
import { I } from 'common/src/designSystem/components/i';
import { Link } from 'common/src/designSystem/components/link';
import { theme } from 'common/src/designSystem/components/stitches';
import { AssignmentInfosQuery } from 'common/src/generated/types';
import { DateTimeService } from 'common/src/services/dateTimeService';
import { isNonEmptyArray } from 'common/src/util/array';
import { useService } from 'common/src/util/dependencies/dependencies';
import { LocaleFormats } from 'common/src/util/luxon';
import { HeaventPaths } from 'common/src/util/paths/heaventPaths';
import { UsersPaths } from 'common/src/util/paths/usersPaths';
import { DateTime } from 'luxon';
import * as React from 'react';
import { TeamMembers } from '../teams/teamMembers';

interface IAccreditAssignHeaderProps {
    badgeColorIcon: IBadgeColorIcon;
    insertedAt: DateTime;
    isPreAssign: boolean;
    showTabs: boolean;
    showTeam: boolean;
    teams: AssignmentInfosQuery['event']['volunteerRegistration']['teams'];
    userInfo: AssignmentInfosQuery['event']['volunteerRegistration']['userInfo'];
}

export const AccreditAssignHeader = (props: IAccreditAssignHeaderProps) => {
    const {
        history,
        translate,
        params: { organizationId, eventId, userInfoId }
    } = useHeavent();
    const dateTimeService = useService(DateTimeService);
    const isTeamLeader = React.useMemo(
        () => props.teams.find((t) => t.leaderUserInfoId === props.userInfo.id),
        [props.teams, props.userInfo]
    );
    const teamMembers = React.useMemo(
        () =>
            props.teams.flatMap((team) =>
                team.members.flatMap((m) => (m.userInfo.id === userInfoId ? [] : [m.userInfo]))
            ),
        [props.teams]
    );

    return (
        <Flex
            align="center"
            css={{
                padding: '$5 $6'
            }}
            gap="4"
        >
            <Box css={{ position: 'relative' }}>
                <Avatar
                    email={props.userInfo.email}
                    image={props.userInfo.picture?.url}
                    name={props.userInfo.name}
                    size={32}
                />

                <Flex
                    align="center"
                    css={{
                        background: theme.colors[`${props.badgeColorIcon.color}100`].value,
                        border: '2px solid white',
                        borderRadius: '16px',
                        bottom: '-6px',
                        color: theme.colors[`${props.badgeColorIcon.color}700`].value,
                        fontSize: '8px',
                        position: 'absolute',
                        right: '-6px'
                    }}
                    height={16}
                    justify="center"
                    width={16}
                >
                    <I icon={props.badgeColorIcon.leftIcon} />
                </Flex>
            </Box>

            <Flex css={{ flex: '1' }} direction="column">
                <Flex align="center" gap="2">
                    <Link
                        to={UsersPaths.USER_INFORMATIONS({ organizationId, eventId, userInfoId })}
                    >
                        <Box
                            css={{
                                cursor: 'pointer',
                                '&:hover': { textDecoration: 'underline' }
                            }}
                            font="gray900 textXl medium"
                        >
                            {props.userInfo.name}
                        </Box>
                    </Link>

                    <Box color="gray500">({props.userInfo.email})</Box>

                    {props.showTeam && isTeamLeader && (
                        <Badge leftIcon="shield">{translate('chef_d_quipe_20641')}</Badge>
                    )}
                </Flex>

                <Flex align="center" gap="2">
                    <Box color="gray500">
                        {dateTimeService.toLocaleString(
                            props.insertedAt,
                            LocaleFormats.DateOnly.MonthLong
                        )}
                    </Box>

                    {props.showTeam && isNonEmptyArray(props.teams) && (
                        <>
                            <Box color="gray500">•</Box>
                            <Box color="gray500">
                                {translate('_quipe_1_07445', props.teams[0].teamCode)}
                            </Box>
                            <Box color="gray500">•</Box>
                            <TeamMembers teamMembers={teamMembers} />
                        </>
                    )}
                </Flex>
            </Flex>

            {props.showTabs && (
                <Box width={320}>
                    <HorizontalTabs
                        selectedItem={props.isPreAssign ? 'preassign' : 'affect'}
                        onChange={(value) => {
                            if (value === 'preassign' && !props.isPreAssign) {
                                history.replace(
                                    HeaventPaths.PRE_ASSIGNMENT(organizationId, eventId, userInfoId)
                                );
                            } else if (value === 'affect' && props.isPreAssign) {
                                history.replace(
                                    HeaventPaths.ASSIGNMENT(organizationId, eventId, userInfoId)
                                );
                            }
                        }}
                    >
                        <Tab item="affect">{translate('affecter_94255')}</Tab>
                        <Tab item="preassign">{translate('pr_affecter_17434')}</Tab>
                    </HorizontalTabs>
                </Box>
            )}
        </Flex>
    );
};
